import { grapheneTimeDeltaToNumeric } from '@/utils/time'
import { parseISO } from 'date-fns'
import type { ResultOf } from 'gql.tada'
import type { SALARY_MONTH_SUMMARY } from '../queries'

type MonthReport = NonNullable<
  ResultOf<typeof SALARY_MONTH_SUMMARY>['monthReport']
>

/**
 * Aggregated month totals used to populate the salary "Sum for [month]"
 * summary cards. All time values are seconds (re-emit with
 * `secondsToHhmm` for display).
 */
export interface SalaryMonthSummary {
  totalWorkSeconds: number
  drivingSeconds: number
  otherWorkSeconds: number
  availabilitySeconds: number
  pauseSeconds: number
  restSeconds: number
  paidRestSeconds: number
  /**
   * Overtime seconds earned inside the month, taken straight from the
   * backend's `overtimeWithinMonth` — the per-activity overtime marking
   * that also feeds the salary base. No reconstruction from the
   * `overtime[]` period-aggregate rows.
   */
  overtimeSeconds: number
  /**
   * True when at least one WorkPeriod computes overtime on a daily basis
   * and none in the month use week/month overtime. Only then is a single
   * month's overtime sum unambiguous, so the overtime summary card is
   * limited to this "day overtime only" case.
   */
  overtimeIsDayOnly: boolean
}

const emptySummary = (): SalaryMonthSummary => ({
  totalWorkSeconds: 0,
  drivingSeconds: 0,
  otherWorkSeconds: 0,
  availabilitySeconds: 0,
  pauseSeconds: 0,
  restSeconds: 0,
  paidRestSeconds: 0,
  overtimeSeconds: 0,
  overtimeIsDayOnly: false,
})

function toSec(timeDelta: string | null | undefined): number {
  if (!timeDelta) return 0
  return grapheneTimeDeltaToNumeric(timeDelta) ?? 0
}

/**
 * Format an aggregated second count as zero-padded `HH:MM`. Hours are
 * not capped at 24, so a 154-hour month renders as `154:07`.
 */
export function formatSecondsAsHhmm(totalSeconds: number): string {
  const sign = totalSeconds < 0 ? '-' : ''
  const abs = Math.abs(totalSeconds)
  const hours = Math.floor(abs / 3600)
  const minutes = Math.floor((abs % 3600) / 60)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${sign}${pad(hours)}:${pad(minutes)}`
}

/**
 * Aggregate per-WorkPeriod activity times plus monthly overtime sums
 * into a single `SalaryMonthSummary`.
 *
 * Walks every WorkPeriod nested inside `monthReport.workPeriodGroups`
 * and sums the time-category fields directly. Overtime is taken from
 * `monthReport.overtimeWithinMonth` (already month-accurate on the
 * backend), not reconstructed from the `overtime[]` display rows.
 *
 * The backend's `monthReport` resolver expands the requested date range
 * to full ISO weeks so it can compute week-based overtime correctly. As
 * a side effect, `workPeriodGroups[].workPeriods[]` can include WPs from
 * the trailing/leading week that falls outside the user-selected month.
 * Callers should pass `rangeStart`/`rangeEnd` so we can filter those
 * out — otherwise activity-time totals would include WPs the user
 * doesn't see in the approve list.
 *
 * - `null`/`undefined` `monthReport` → zeroed summary.
 * - Missing/empty TimeDelta strings contribute 0.
 * - WorkPeriods whose `datetimeStart` is outside `[rangeStart, rangeEnd)`
 *   are skipped (range filter only applies when both bounds are given).
 *
 * @param monthReport `monthReport` payload from `SALARY_MONTH_SUMMARY`.
 * @param rangeStart Lower bound (inclusive) for WP `datetimeStart`.
 * @param rangeEnd Upper bound (inclusive) for WP `datetimeStart`.
 * @param approvedFilter When given, only count WorkPeriods signed off by the
 *   enabled approval flows ("only approved" toggle). `totalWorkSeconds` then
 *   comes from the backend's `totalWorkTimeApproved` (same flow semantics),
 *   and the per-category activity times skip unapproved WPs. Overtime sums
 *   are always approved-only on the backend and are unaffected.
 */
export interface ApprovedFilter {
  driverApprove: boolean
  managerApprove: boolean
}

function isApproved(
  wp: { approvedByUser: boolean; approvedByManager: boolean },
  filter: ApprovedFilter
): boolean {
  if (filter.driverApprove && !wp.approvedByUser) return false
  if (filter.managerApprove && !wp.approvedByManager) return false
  return true
}

export function aggregateMonthSummary(
  monthReport: MonthReport | null | undefined,
  rangeStart?: Date,
  rangeEnd?: Date,
  approvedFilter?: ApprovedFilter
): SalaryMonthSummary {
  const result = emptySummary()
  if (!monthReport) return result

  result.totalWorkSeconds = toSec(
    approvedFilter
      ? monthReport.totalWorkTimeApproved
      : monthReport.totalWorkTime
  )

  result.overtimeSeconds = toSec(monthReport.overtimeWithinMonth)

  const startMs = rangeStart?.getTime()
  const endMs = rangeEnd?.getTime()

  // A WorkPeriod can appear in multiple `workPeriodGroups` (e.g. grouped
  // by vehicle, calendar item, or absence day). Dedupe by `id` so its
  // activity times aren't summed twice.
  const seen = new Set<string>()
  let anyDayOvertime = false
  let anyWeekOrMonthOvertime = false
  for (const group of monthReport.workPeriodGroups ?? []) {
    for (const wp of group.workPeriods ?? []) {
      if (seen.has(wp.id)) continue
      seen.add(wp.id)

      if (startMs !== undefined && endMs !== undefined) {
        const wpMs = parseISO(wp.datetimeStart).getTime()
        if (wpMs < startMs || wpMs > endMs) continue
      }

      if (approvedFilter && !isApproved(wp, approvedFilter)) continue

      const t = wp.activityTimes
      if (t) {
        result.drivingSeconds += toSec(t.timeDriving)
        result.otherWorkSeconds += toSec(t.timeOtherWork)
        result.availabilitySeconds += toSec(t.timeAvailability)
        result.pauseSeconds += toSec(t.timePause)
        result.restSeconds += toSec(t.timeRest)
        result.paidRestSeconds += toSec(t.timePaidRest)
      }
      const setting = wp.salarySetting
      if (setting?.useWeekOvertime || setting?.useMonthOvertime) {
        anyWeekOrMonthOvertime = true
      }
      if (setting?.useDayOvertime) anyDayOvertime = true
    }
  }

  result.overtimeIsDayOnly = anyDayOvertime && !anyWeekOrMonthOvertime

  return result
}
